import type {
  CreateProjectRequest,
  ProjectDetail,
  ProjectSummary,
  Scene,
  UpdateProjectRequest,
  UpdateSceneRequest,
} from '@gc/shared';
import { apiRequest } from './client.js';

export type Pagina<T> = { readonly data: T[]; readonly nextCursor: string | null };

/**
 * El listado devuelve `meta.nextCursor`, así que no puede usar `apiRequest`,
 * que desenvuelve solo `data`.
 */
export async function listarProyectos(opciones: {
  readonly includeArchived?: boolean;
  readonly limit?: number;
  readonly cursor?: string;
} = {}): Promise<Pagina<ProjectSummary>> {
  const params = new URLSearchParams();
  if (opciones.includeArchived) params.set('includeArchived', 'true');
  if (opciones.limit !== undefined) params.set('limit', String(opciones.limit));
  if (opciones.cursor !== undefined) params.set('cursor', opciones.cursor);
  const query = params.size > 0 ? `?${params.toString()}` : '';

  const respuesta = await apiRequest<{ data: ProjectSummary[]; meta: { nextCursor: string | null } }>(
    `/projects${query}`,
    { raw: true },
  );
  return { data: respuesta.data, nextCursor: respuesta.meta.nextCursor };
}

export function crearProyecto(input: CreateProjectRequest): Promise<ProjectDetail> {
  return apiRequest<ProjectDetail>('/projects', { method: 'POST', body: input });
}

export function obtenerProyecto(id: string): Promise<ProjectDetail> {
  return apiRequest<ProjectDetail>(`/projects/${id}`);
}

export function actualizarProyecto(
  id: string,
  input: UpdateProjectRequest,
): Promise<ProjectDetail> {
  return apiRequest<ProjectDetail>(`/projects/${id}`, { method: 'PATCH', body: input });
}

export function archivarProyecto(id: string): Promise<ProjectSummary> {
  return apiRequest<ProjectSummary>(`/projects/${id}`, { method: 'DELETE' });
}

export function restaurarProyecto(id: string): Promise<ProjectSummary> {
  return apiRequest<ProjectSummary>(`/projects/${id}/restore`, { method: 'POST' });
}

export function listarEscenas(projectId: string): Promise<Scene[]> {
  return apiRequest<Scene[]>(`/projects/${projectId}/scenes`);
}

export function actualizarEscena(
  projectId: string,
  sceneId: string,
  input: UpdateSceneRequest,
): Promise<Scene[]> {
  return apiRequest<Scene[]>(`/projects/${projectId}/scenes/${sceneId}`, {
    method: 'PATCH',
    body: input,
  });
}

export function eliminarEscena(
  projectId: string,
  sceneId: string,
  expectedRevision: number,
): Promise<Scene[]> {
  return apiRequest<Scene[]>(`/projects/${projectId}/scenes/${sceneId}`, {
    method: 'DELETE',
    body: { expectedRevision },
  });
}

export function reordenarEscenas(
  projectId: string,
  sceneIds: readonly string[],
  expectedRevision: number,
): Promise<Scene[]> {
  return apiRequest<Scene[]>(`/projects/${projectId}/scenes/reorder`, {
    method: 'POST',
    body: { expectedRevision, sceneIds },
  });
}
